import { Button } from '@monksflow/monks-ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BriefsPaginationProps {
  /** 1-indexed current page. */
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * Compact "Page X of Y · ← Prev · Next →" pagination control for the
 * briefs list view.
 *
 * A compact current/total + prev/next pattern rather than the numbered page
 * list of Tessra's `Pagination` family. Both prev/next
 * are real `<button>` elements (not anchors) so disabled semantics propagate
 * to assistive tech without extra `aria-` plumbing.
 *
 * Hide-when-single-page: returns `null` when `totalPages <= 1`. The page
 * orchestrator can still pre-gate this component for symmetry, but the
 * component is defensively safe to mount unconditionally.
 */
export function BriefsPagination(props: BriefsPaginationProps) {
  const { t } = useTranslation('projects');

  const { currentPage, totalPages, onPageChange } = props;

  if (totalPages <= 1) {
    return null;
  }

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  const handlePrev = () => {
    if (isFirstPage) return;
    onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (isLastPage) return;
    onPageChange(currentPage + 1);
  };

  return (
    <nav
      aria-label={t('projectDetail.briefsPagination.briefsPagination')}
      data-testid="briefs-pagination"
      className="flex items-center justify-center gap-3"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={t('projectDetail.briefsPagination.previousPage')}
        disabled={isFirstPage}
        onClick={handlePrev}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <span className="text-sm text-muted-foreground" aria-live="polite">
        {t('projectDetail.briefsPagination.pageOf', { currentPage, totalPages })}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={t('projectDetail.briefsPagination.nextPage')}
        disabled={isLastPage}
        onClick={handleNext}
      >
        <ChevronRight className="size-4" />
      </Button>
    </nav>
  );
}
